'use client';

import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, List, Typography } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { Link } from './components/Link';
import { VerticalLinearProgressBar } from './components/VerticalLinearProgressBar';
import type { TableOfContentsHeadingProps } from './types';
import { useDesktopToc } from './useDesktopToc';

type Props = {
  distanceToSectionWrapper: number;
  headingLevel: number;
  headings: TableOfContentsHeadingProps[];
  tocId: string;
  activeId: string;
  setActiveId: Dispatch<SetStateAction<string>>;
  fireNavigateToSectionPageAction: (
    overrideProps?: Record<string, string | object>,
    callback?: () => void,
  ) => void;
};

export function DesktopTOCWithProgressBar(props: Props) {
  const { headings, tocId, activeId } = props;
  const { handleClick, handleRef, progress } = useDesktopToc(props);

  return (
    <Box
      id={tocId}
      component="nav"
      aria-label="table of contents"
      sx={{
        backgroundColor: 'common.white',
        borderRadius: 4,
        margin: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FormatListBulletedIcon sx={{ color: 'primary.main' }} />
        <Typography component="span" id="on-this-page" sx={{ fontSize: 16, fontWeight: 500 }}>
          Table of contents
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <VerticalLinearProgressBar progress={progress} />
        <List
          component="ol"
          aria-labelledby="on-this-page"
          disablePadding
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {headings.map(heading => (
            <List component="li" key={heading.id} disablePadding>
              <Link
                isSelected={activeId === heading.id}
                headingId={heading.id}
                handleRef={handleRef}
                onClick={handleClick}
                variant="linear-progress-variant"
              >
                {heading.header}
              </Link>
            </List>
          ))}
        </List>
      </Box>
    </Box>
  );
}
